import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";

import Button from "./Button";

import CupcakeIcon from "../../assets/icons/CupcakeIcon";
import { LinkStatus } from "../../services/addLink";
import { useMutation } from "@tanstack/react-query";
import { updateTagLinksCount } from "../../services/updateTagLinksCount";
import { QueryKeys } from "../../services/types";
import { updateLinkStatus } from "../../services/updateLinkStatus";
import { updateUserPoints } from "../../services/updateUserPoints";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Session } from "@supabase/supabase-js";
import Modal from "../molecules/Modal/Modal";
import { updateLinkLikes } from "../../services/updateLinkLikes";
import { updateUserLikedLinks } from "../../services/updateUserLikedLinks";
import EditIcon from "../../assets/icons/EditIcon";
import { deleteLink } from "../../services/deleteLink";
import StatusInfo, { StatusInfoVariant } from "./StatusInfo";

type SingleLinkProps = {
  link: {
    created_at: string;
    description: string | null;
    id: number;
    status: string | null;
    tag_id: number | null;
    title: string | null;
    url: string | null;
    user_id: string | null;
    likes: number;
    author_username: string;
    published_at: string | Date | null;
  };
  tagName?: string | null;
  tagLinksCount?: number;
  pageType: "home" | "profile" | "liked";
  refetchPublishedLinks?: any;
};

enum ModalType {
  STATUS = "status",
  DELETE = "delete",
}

const SingleLink = ({
  link,
  tagName,
  pageType,
  tagLinksCount,
  refetchPublishedLinks,
}: SingleLinkProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [currentLikes, setCurrentLikes] = useState(0);
  const [userLikedLinks, setUserLikedLinks] = useState<number[]>([]);
  const [isCopied, setIsCopied] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>(ModalType.STATUS);

  const [session] = useOutletContext<Session[]>();

  const navigate = useNavigate();

  const theme = useTheme();

  const htmlLinkId = `link-${link.id}`;

  useEffect(() => {
    let timeout: string | number | NodeJS.Timeout | undefined;
    if (isCopied) {
      timeout = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isCopied]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link.url || "").then(() => {
      setIsCopied(true);
    });
  };

  useEffect(() => {
    if (session?.user?.user_metadata?.likedLinks) {
      setUserLikedLinks(session.user.user_metadata.likedLinks);
    }
  }, [session.user.user_metadata.likedLinks]);

  useEffect(() => {
    if (link) {
      setCurrentLikes(link.likes);
    }
  }, [link, link.likes]);

  useEffect(() => {
    userLikedLinks?.forEach((id: number) => {
      if (Number(id) === Number(link.id)) {
        setIsLiked(true);
      }
    });
  }, [link.id, userLikedLinks]);

  const { mutateAsync: deleteLinkMutation, isLoading: isLinkDeleting } =
    useMutation({
      mutationFn: deleteLink,
      mutationKey: [QueryKeys.USER_LINKS],
    });

  const handleDeleteLink = () => {
    deleteLinkMutation(link.id).then((res) => {
      if (res.status && res.status < 300) {
        setIsModalOpen(false);
        navigate(0);
      }
    });
  };

  const { mutateAsync: updateLinkStatusMutation } = useMutation({
    mutationFn: updateLinkStatus,
    mutationKey: [QueryKeys.USER_LINKS],
  });

  const { mutateAsync: updateTagLinksCountMutation } = useMutation({
    mutationFn: updateTagLinksCount,
    mutationKey: [QueryKeys.TAGS],
  });

  const { mutateAsync: updateUserPointsMutation } = useMutation({
    mutationFn: updateUserPoints,
    mutationKey: [QueryKeys.POINTS],
  });

  const handleChangeLinkToPublic = async () => {
    setIsLoading(true);
    setIsModalOpen(false);
    await updateLinkStatusMutation({
      linkId: Number(link.id),
      newStatus: LinkStatus.PUBLISHED,
    }).then(async (res) => {
      if (res.status === 200 || res.status === 201) {
        await updateTagLinksCountMutation({
          tagId: Number(link.tag_id),
          newLinksCount: Number(tagLinksCount) + 1,
        });
        await updateUserPointsMutation({
          newPointsCount: Number(session?.user.user_metadata?.points) + 2,
          newTotalPointsCount:
            Number(session.user.user_metadata.total_points) + 2,
        });
        navigate(0);
      }
    });
    setIsLoading(false);
  };

  const handleChangeLinkToPrivate = async () => {
    setIsLoading(true);
    setIsModalOpen(false);
    await updateLinkStatusMutation({
      linkId: Number(link.id),
      newStatus: LinkStatus.PRIVATE,
    }).then(async (res) => {
      if (res.status === 200 || res.status === 201) {
        await updateTagLinksCountMutation({
          tagId: Number(link.tag_id),
          newLinksCount: Number(tagLinksCount) - 1,
        });
        await updateUserPointsMutation({
          newPointsCount: Number(session?.user.user_metadata?.points) - 2,
        });
        navigate(0);
      }
    });
    setIsLoading(false);
  };

  const { mutateAsync: updateLinkLikesMutation } = useMutation({
    mutationFn: updateLinkLikes,
    mutationKey: [
      QueryKeys.ALL_LINKS,
      QueryKeys.USER_LIKED_LINKS,
      QueryKeys.USER_LINKS,
    ],
  });

  const { mutateAsync: updateUserLikedLinksMutation } = useMutation({
    mutationFn: updateUserLikedLinks,
    mutationKey: [QueryKeys.USER_METADATA],
  });

  const handleLikeLink = async () => {
    setIsLiking(true);
    if (!isLiked) {
      setCurrentLikes((prev) => prev + 1);
      setIsLiked(true);
      setUserLikedLinks([...userLikedLinks, link.id]);

      updateLinkLikesMutation({
        newLikesCount: Number(link.likes) + 1,
        linkId: link.id,
      }).then(async (res) => {
        if (res.status === 200 || res.status === 201) {
          await updateUserLikedLinksMutation({
            newLikedLinks: [...userLikedLinks, link.id],
          });
          await refetchPublishedLinks();

          navigate(0);
          sessionStorage.setItem("lastLikedLink", htmlLinkId);
          setIsLiking(false);
        }
      });
    } else if (link.likes > 0 || currentLikes > 0) {
      setCurrentLikes((prev) => prev - 1);
      setIsLiked(false);
      setUserLikedLinks((prev) => prev.filter((id) => id !== link.id));

      updateLinkLikesMutation({
        newLikesCount: Number(link.likes) - 1,
        linkId: link.id,
      }).then(async (res) => {
        if (res.status === 200 || res.status === 201) {
          await updateUserLikedLinksMutation({
            newLikedLinks: userLikedLinks.filter((id) => id !== link.id),
          });
          await refetchPublishedLinks();

          navigate(0);
          sessionStorage.setItem("lastLikedLink", htmlLinkId);
          setIsLiking(false);
        }
      });
    }
  };

  const lastLikedLinkId = sessionStorage.getItem("lastLikedLink");
  useEffect(() => {
    if (lastLikedLinkId) {
      const likedLink = document.getElementById(lastLikedLinkId);
      if (likedLink) {
        likedLink?.scrollIntoView();
        sessionStorage.removeItem("lastLikedLink");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getModalContent = (type: ModalType) => {
    if (type === ModalType.STATUS) {
      if (link.status === LinkStatus.PRIVATE) {
        return {
          text: "Change the link status to public and earn 2 linksooons!",
          buttonText: "Change to Public",
          func: handleChangeLinkToPublic,
          buttonColor: theme.colors.privateText,
        };
      } else {
        return {
          text: "Change the link status to private, you will lose 2 linksooons :(",
          buttonText: "Change to Private",
          func: handleChangeLinkToPrivate,
          buttonColor: theme.colors.publicText,
        };
      }
    } else if (type === ModalType.DELETE) {
      return {
        text: `Delete link: ${link.title}`,
        buttonText: "Delete",
        func: handleDeleteLink,
        buttonColor: theme.colors.error,
      };
    }
  };

  return (
    <>
      <StyledSingleLink id={htmlLinkId}>
        <StyledTopInfoWrapper>
          <StyledAuthorAndDate>
            {pageType === "home" && (
              <StyledAuthor>{link.author_username}</StyledAuthor>
            )}
            <StyledCreatedAt>
              {pageType === "home" && link.published_at
                ? new Date(link.published_at).toLocaleString()
                : new Date(link.created_at).toLocaleString()}
            </StyledCreatedAt>
          </StyledAuthorAndDate>

          {pageType === "profile" && (
            <>
              <StyledStatus
                status={link.status}
                onClick={() => {
                  setIsModalOpen(true);
                  setModalType(ModalType.STATUS);
                }}
              >
                {link.status}
              </StyledStatus>
            </>
          )}
        </StyledTopInfoWrapper>

        <StyledTagEditWrapper>
          <StyledTagName>{tagName}</StyledTagName>

          {pageType === "profile" && (
            <StyledEditButton onClick={() => setIsEditModalOpen(true)}>
              <EditIcon color={theme.colors.text} width={21} height={21} />
            </StyledEditButton>
          )}

          {isEditModalOpen && (
            <>
              <StyledModal>
                <ul>
                  <li className="disabled">Edit</li>
                  <li className="delete">
                    <button
                      onClick={() => {
                        setModalType(ModalType.DELETE);
                        setIsEditModalOpen(false);
                        setIsModalOpen(true);
                      }}
                    >
                      Delete
                    </button>
                  </li>
                </ul>
              </StyledModal>
              <StyledBackdrop onClick={() => setIsEditModalOpen(false)} />
            </>
          )}
        </StyledTagEditWrapper>

        <StyledTitle>{link.title}</StyledTitle>
        <StyledDescription>{link.description}</StyledDescription>

        <StyledURL>{link.url}</StyledURL>

        <StyledButtonsWrapper>
          {pageType === "profile" || link.user_id === session.user.id ? (
            <StyledLikesInfo>{link.likes}</StyledLikesInfo>
          ) : (
            <Button
              text={currentLikes.toString()}
              onClick={() => {
                handleLikeLink();
              }}
              icon={
                <CupcakeIcon
                  color={isLiked ? theme.colors.primary : theme.colors.text}
                />
              }
              width="fit-content"
              backgroundColor="transparent"
              color={isLiked ? theme.colors.primary : theme.colors.text}
              margin="0"
              padding="5px 10px"
              disabled={isLiking}
            />
          )}

          <Button
            text={isCopied ? "Copied" : "Copy"}
            onClick={() => handleCopyLink()}
            width="fit-content"
            backgroundColor="transparent"
            color={theme.colors.text}
            margin="0"
            padding="5px 10px"
          />

          <Button
            text="Visit"
            onClick={() => {
              if (link.url) {
                window.open(link.url, "_blank");
              }
            }}
            width="fit-content"
            margin="0"
            padding="5px 10px"
          />
        </StyledButtonsWrapper>
      </StyledSingleLink>

      <Modal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        text={getModalContent(modalType)?.text || ""}
        buttonText={getModalContent(modalType)?.buttonText || ""}
        onButtonClick={() => getModalContent(modalType)?.func()}
        buttonColor={getModalContent(modalType)?.buttonColor}
      />

      {isLoading ||
        (isLinkDeleting && (
          <StatusInfo variant={StatusInfoVariant.LOADING} text="Loading" />
        ))}
    </>
  );
};

const StyledSingleLink = styled.section`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.singleLinkBg};
  border: 1px solid ${({ theme }) => theme.colors.text};
  padding: 12px;
  border-radius: ${({ theme }) => theme.radii.default};

  width: 100%;
  max-width: 500px;

  @media (min-width: 600px) {
    width: calc(50% - 10px);
  }
  @media (min-width: 950px) {
    width: calc(33% - 11px);
  }
`;

const StyledTopInfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
`;

const StyledAuthorAndDate = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledAuthor = styled.span`
  font-size: ${({ theme }) => theme.fontSize.S};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const StyledStatus = styled.button<{ status: string | null }>`
  background-color: ${({ status, theme }) =>
    status === "private" ? theme.colors.privateBg : theme.colors.publicBg};
  color: ${({ status, theme }) =>
    status === "private" ? theme.colors.privateText : theme.colors.publicText};
  padding: 2px 5px;
  border-radius: ${({ theme }) => theme.radii.small};
  cursor: pointer;
  border: none;
  font-size: ${({ theme }) => theme.fontSize.S};
`;

const StyledCreatedAt = styled.small`
  font-size: ${({ theme }) => theme.fontSize.XS};
`;

const StyledTagEditWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  margin: 2px 0;
  position: relative;
`;

const StyledTagName = styled.span`
  background-color: ${({ theme }) => theme.colors.tagBg};
  color: ${({ theme }) => theme.colors.tagText};
  width: fit-content;
  padding: 2px 5px;
  border-radius: ${({ theme }) => theme.radii.small};
  margin: 7px 0;
`;

const StyledEditButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin: 0;
`;

const StyledTitle = styled.strong`
  margin-bottom: 3px;
`;

const StyledDescription = styled.span``;

const StyledURL = styled.small`
  color: ${({ theme }) => theme.colors.textInactive};
  font-size: ${({ theme }) => theme.fontSize.XS};
  margin-top: 5px;
`;

const StyledButtonsWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
`;

const StyledLikesInfo = styled.span`
  font-size: ${({ theme }) => theme.fontSize.M};
  color: ${({ theme }) => theme.colors.text};
  padding: 5px 10px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const StyledModal = styled.div<{ isDisabled?: string }>`
  position: absolute;
  bottom: -95px;
  right: 0;
  z-index: 1000;
  background-color: ${({ theme }) => theme.colors.text};
  padding: 0 20px;
  border-radius: ${({ theme }) => theme.radii.default};

  ul {
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;

    li {
      padding: 5px;
      color: ${({ theme }) => theme.colors.dark};
      font-size: ${({ theme }) => theme.fontSize.M};

      &.disabled {
        cursor: not-allowed;
        pointer-events: none;
        opacity: 0.3;
      }

      &.delete {
        button {
          color: ${({ theme }) => theme.colors.error};
        }
      }

      button {
        background-color: transparent;
        border: none;
        font-size: ${({ theme }) => theme.fontSize.M};
        cursor: pointer;
      }
    }
  }
`;

const StyledBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: black;
  opacity: 0.1;
  z-index: 999;
`;

export default SingleLink;
